import { peUrlParams } from './peUrlParams'
import { clearFullpageMessages, showFullpageMessage } from './showFullpageMessage'
import { SafariAgaveUnsupportedError, CookiesDisabledError } from './initErrors'

function showAwaitingPopup () {
  showFullpageMessage({
    icon: 'restart',
    iconSpin: true,
    subject: 'Awaiting permission',
    message: "Please complete the access permission in the window. If you don't see it, check the popup wasn't blocked by your browser.",
    abort: {
      label: 'Restart',
      handler: () => window.location.reload()
    }
  })
}

function popupFirstPartyRequest () {
  return new Promise((resolve, reject) => {
    const popup = window.open('/storage-access', 'Poll Everywhere Embed Permissions', 'resizable,scrollbars,status')
    if (popup) {
      const onMessage = (message) => {
        const event = message.data
        if (event.type === 'storage-access') {
          if (event.success) {
            resolve()
          } else {
            reject(new Error(`Failed to get storage access: ${event.error}`))
          }
          window.removeEventListener('message', onMessage, false)
        }
      }

      window.addEventListener('message', onMessage, false)
      showAwaitingPopup()
    } else {
      reject(new Error('Could not open storage access window'))
    }
  })
}

function initialMessageWhenStorageBlocked () {
  const params = peUrlParams()

  return new Promise((resolve, reject) => {
    showFullpageMessage({
      icon: 'logo',
      iconType: 'legacy-glyph',
      subject: 'Welcome to Poll Everywhere',
      message: "Participate in live activities, submit feedback, and weigh in on discussions. We care about your privacy, so we'll need your permission to access website data before collecting your response.",
      modifier: params.has('whitelabel') ? null : 'pe-theme',
      resolve: {
        label: 'Get started',
        handler: () => popupFirstPartyRequest().then(resolve).catch(reject)
      }
    })
  })
}

function finalAccessRequest () {
  return new Promise((resolve, reject) => {
    showFullpageMessage({
      icon: 'info',
      subject: 'Grant permission',
      message: `
        <p>To submit a response to this live activity, allow Poll Everywhere to access your website data.<p>
        <p><em>Note: A permission prompt will appear the first time you grant Poll Everywhere access, and may again if you haven't visited in a few days.</em></p>
      `,
      resolve: {
        label: 'Allow',
        handler: () => document.requestStorageAccess().then(resolve).catch(reject)
      }
    })
  })
}

function isSafari () {
  // the chrome UA contains both Chrome and Safari
  let isSafari = navigator.userAgent.indexOf('Safari') > -1
  const isChrome = navigator.userAgent.indexOf('Chrome') > -1
  if (isSafari && isChrome) {
    isSafari = false
  }
  return isSafari
}

function isAgaveDomain () {
  const regex = /(?:office\.polleverywhere\.com|-agave\.ops\.pe)/
  return regex.test(window.location.href)
}

function canRequestStorageAccess () {
  return !!document.requestStorageAccess
}

function areCookiesEnabled () {
  // The _only_ cookies supported in iframes in Chrome are secure samesite=none
  document.cookie = 'name=pecheck;SameSite=None;Secure'
  return document.cookie.indexOf('pecheck') !== -1
}

async function isAgaveSafariWithoutStorage () {
  // This is a short circuit failure scenario, users will never get the option to allow storage access
  // if the iframe doesn't have property sandbox="... allow-storage-access-by-user-activation ..."
  // there is no way to check if the parent iframe supports this functionality to begin with.
  // We cannot support agave iframed within Safari until Microsoft adds the above property
  return isAgaveDomain() &&
    isSafari() &&
    !await hasStorageAccess()
}

export async function requestStorageAccess () {
  if (await isAgaveSafariWithoutStorage()) {
    throw new SafariAgaveUnsupportedError()
  }
  if (!canRequestStorageAccess() && !areCookiesEnabled()) {
    throw new CookiesDisabledError()
  }
  await initialMessageWhenStorageBlocked()
  await finalAccessRequest()
  clearFullpageMessages()
}

export async function hasStorageAccess () {
  if (document.hasStorageAccess) {
    const hasAccess = await document.hasStorageAccess()
    return hasAccess
  } else {
    return areCookiesEnabled()
  }
}
